import React from "react";
import { Navigate } from "react-router";

import {
  ROUTES,
  USER_FEATURES,
} from "sharedKernel/constants";
import { canAccessMeetingRoom } from "sharedKernel/auth/behavior";
import { getHubRoute } from "sharedKernel/handlers";
import { IHubRouteBuilder } from "./contract";

import { ProgramForm } from "domain/feature/hub/company/challenge/program";
import { HubEvaluatorChallengeSolutionsPage } from 'main/pages/hub/evaluator/challenge/solutions';
import { TracksPage } from "main/pages/tracks/adm/all";
import { HubDashboardPage } from 'main/pages/hub/dashboard'
import { UserConfigPage } from 'main/pages/hub/user-config'
import { HubCompanyChallengePage } from 'main/pages/hub/company/challenge/all'
import { HubCompanyCalendarPage } from 'main/pages/hub/company/calendarpage'
import { HubCompanyChallengeTemplatePage } from 'main/pages/hub/company/challenge/template'
import { HubCompanyNewChallengePage } from 'main/pages/hub/company/challenge/new'
import { HubCompanyChallengeEditPage } from 'main/pages/hub/company/challenge/edit'
import { HubChallengeGetPage } from 'main/pages/hub/company/challenge/get'
import { HubCompanyEvaluatorsPage } from 'main/pages/hub/company/challenge/evaluators'
import { HubCompanyChallengeSolutionsPage } from 'main/pages/hub/company/challenge/solutions'
import { HubStartupChallengePage } from 'main/pages/hub/startup/challenge/all'
import { HubStartupChallengeGet } from 'main/pages/hub/startup/challenge/get'
import { HubStartupChallengeApplicationFormPage } from 'main/pages/hub/startup/challenge/apply'
import { HubStartupIdeaPage } from 'main/pages/hub/startup/idea/all'
import { HubStartupIdeaGet } from 'main/pages/hub/startup/idea/get'
import { HubStartupChallengeApplicationEditFormPage } from 'main/pages/hub/startup/challenge/edit'
import { HubEvaluatorChallengePage } from 'main/pages/hub/evaluator/challenge/all'
import { HubEvaluatorIdeaEvaluatePage } from 'main/pages/hub/evaluator/idea/evaluate'
import { HubEvaluatorInvitesPage } from 'main/pages/hub/evaluator/invites'
import { HubEvaluatorChallengeInfoPage } from 'main/pages/hub/evaluator/challenge/visualization'
import { HubCompanyEcosystemPage } from 'main/pages/hub/company/ecosystem'
import { HubCompanyChallengeSolutionsInfoPage } from 'main/pages/hub/company/challenge/solutions/info'
import { HubCompanyEventsPage } from 'main/pages/hub/company/events/all'
import { HubCompanyNewEventPage } from 'main/pages/hub/company/events/new'
import { HubCompanyEventEditPage } from 'main/pages/hub/company/events/edit'
import { HubEventGetPage } from 'main/pages/hub/company/events/get'
import { HubCompanyScoutingPage } from 'main/pages/hub/company/scouting/all'
import { HubCompanyNewScoutingPage } from 'main/pages/hub/company/scouting/new'
import { HubScoutingGetPage } from 'main/pages/hub/company/scouting/get'
import { HubScoutingStartupPage } from 'main/pages/hub/company/scouting/startup'
import { HubStartupGetPage } from 'main/pages/hub/company/ecosystem/startup'
import { HubPublicAdministrationGetPage } from 'main/pages/hub/company/ecosystem/public-administration'
import { CreateTrackForm } from "main/pages/tracks/adm/create";
import { EditTrackForm } from "main/pages/tracks/adm/edit";
import { LearningPage } from "main/pages/tracks/learning";
import { LearningLessonPage } from "main/pages/tracks/learning/lesson";
import { TrackGetPage } from "main/pages/tracks/adm/get";
import { HubStartupEventsPage } from "main/pages/hub/startup/event/all";
import { HubResolverEventGetPage } from "main/pages/hub/startup/event/get";
import { HubStartupOpportunityPage } from "main/pages/hub/startup/opportunity/all";
import { HubStartupOpportunityGetPage } from "main/pages/hub/startup/opportunity/get";
import { trans } from "sharedKernel/i18n";
import { HubCompanyPublicOrgansPage } from "main/pages/hub/company/public-organs";
import { HubCompanyCityHallMembersPage } from "main/pages/hub/company/city-hall-members";
import { TracksAvailablePage } from "main/pages/tracks/startup/all";
import { TrackAvailableDetailsPage } from "main/pages/tracks/startup/details";
import { TrackAvailableLessonPage } from "main/pages/tracks/startup/lesson";
import { HubCompanyCityHallMainPage } from "main/pages/hub/company/city-hall-main";
import { HubCompanyManageMeetingRoomPage } from 'main/pages/hub/company/meeting-room/manage-meeting-room'
import { HubCompanyMeetingRoomSettingsPage } from 'main/pages/hub/company/meeting-room/settings'
import { HubCompanyNewMeetingRoomPage } from 'main/pages/hub/company/meeting-room/manage-meeting-room/new'
import { HubCompanyMeetingRoomDetailPage } from 'main/pages/hub/company/meeting-room/manage-meeting-room/detail'
import { HubCompanyEditMeetingRoomPage } from 'main/pages/hub/company/meeting-room/manage-meeting-room/edit'
import { HubCompanyMeetingRoomReservedPage } from 'main/pages/hub/company/meeting-room/meeting-room-reservation'
import { HubCompanyMeetingRoomRequestsPage } from 'main/pages/hub/company/meeting-room/meeting-room-solicitation'
import { HubCompanyMeetingRoomReservePage } from 'main/pages/hub/company/meeting-room/book-meeting-room'
import { HubCompanyMeetingRoomMyReservationsPage } from 'main/pages/hub/company/meeting-room/my-reservation'
import { HubPublicAdministrationPage } from "main/pages/hub/public-administration";


const MeetingRoomAccessGuard = ({
  children,
}: {
  children: React.ReactElement;
}): React.ReactElement =>
  canAccessMeetingRoom()
    ? children
    : <Navigate to={getHubRoute(ROUTES.HUB.DASHBOARD)} replace />;

const meetingRoomAllowedOnly = (element: React.ReactElement): React.ReactElement => (
  <MeetingRoomAccessGuard>{element}</MeetingRoomAccessGuard>
);

export const hubRoutes: IHubRouteBuilder[] = [
  {
    path: ROUTES.HUB.DASHBOARD,
    breadcrumb: "Dashboard",
    component: <HubDashboardPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.USER_CONFIG,
    breadcrumb: "Configurações de usuário",
    component: <UserConfigPage />,
    features: [
      USER_FEATURES.CHALLENGE,
      USER_FEATURES.IDEA,
      USER_FEATURES.EVALUATION,
      USER_FEATURES.ADMIN_CITY_HALL,
      USER_FEATURES.CITY_HALL,
      USER_FEATURES.PUBLIC_ADMINISTRATION,
      'caixa_employee',
    ],
  },
  {
    path: ROUTES.HUB.PUBLIC_ADMINISTRATION,
    breadcrumb: "Adicionar membros",
    component: <HubPublicAdministrationPage />,
    features: [USER_FEATURES.PUBLIC_ADMINISTRATION, USER_FEATURES.IDEA, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.CALENDAR,
    breadcrumb: "Calendário",
    component: <HubCompanyCalendarPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.CHALLENGE_LIST,
    breadcrumb: "Programas",
    component: <HubCompanyChallengePage />,
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.ECOSYSTEM,
    breadcrumb: "Ecossistemas",
    component: <HubCompanyEcosystemPage />,
    features: [
      USER_FEATURES.CHALLENGE,
      USER_FEATURES.IDEA,
      USER_FEATURES.CITY_HALL,
      USER_FEATURES.ADMIN_CITY_HALL,
      'caixa_employee',
    ],
  },
  {
    path: ROUTES.HUB.COMPANY.CHALLENGE_TEMPLATE,
    breadcrumb: "Template do programa",
    component: <HubCompanyChallengeTemplatePage />,
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.NEW_CHALLENGE,
    breadcrumb: "Criar programa",
    component: <HubCompanyNewChallengePage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.REFACTORY_NEW_CHALLENGE,
    breadcrumb: "Criar programa",
    component: <ProgramForm />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.REFACTORY_NEW_CHALLENGE_EDIT,
    breadcrumb: "Editar programa",
    component: <ProgramForm />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.GET_CHALLENGE,
    breadcrumb: "Visualizar programa",
    component: <HubChallengeGetPage />,
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.EDIT_CHALLENGE,
    breadcrumb: "Editar programa",
    component: <HubCompanyChallengeEditPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.CHALLENGE_EVALUATORS,
    breadcrumb: "Gerenciar avaliadores",
    component: <HubCompanyEvaluatorsPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.CHALLENGE_SOLUTIONS,
    breadcrumb: "Gerenciar inscrições",
    component: <HubCompanyChallengeSolutionsPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.EVENTS_LIST,
    breadcrumb: "Eventos",
    component: <HubCompanyEventsPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.NEW_EVENT,
    breadcrumb: "Criar Evento",
    component: <HubCompanyNewEventPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.EDIT_EVENT,
    breadcrumb: "Editar Evento",
    component: <HubCompanyEventEditPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":eventId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.GET_EVENT,
    breadcrumb: "Visualizar Evento",
    component: <HubEventGetPage />,
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":eventId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.SCOUTING_LIST,
    breadcrumb: "Oportunidades",
    component: <HubCompanyScoutingPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.NEW_SCOUTING,
    breadcrumb: "Criar Oportunidade",
    component: <HubCompanyNewScoutingPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.EDIT_SCOUTING,
    breadcrumb: "Editar Oportunidade",
    component: <HubCompanyNewScoutingPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":scoutingId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.GET_SCOUTING,
    breadcrumb: "Visualizar Oportunidade",
    component: <HubScoutingGetPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":scoutingId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.SCOUTING_STARTUP,
    breadcrumb: "Detalhes da startup",
    component: <HubScoutingStartupPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":startupId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.PUBLIC_ORGANS,
    breadcrumb: "Organizações públicas",
    component: <HubCompanyPublicOrgansPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.CITY_HALL_MEMBERS,
    breadcrumb: "Adicionar membros",
    component: <HubCompanyCityHallMembersPage />,
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.CITY_HALL_MAIN,
    breadcrumb: "Painel da Prefeitura",
    component: <HubCompanyCityHallMainPage />,
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, USER_FEATURES.CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.STARTUP.CHALLENGE_LIST,
    breadcrumb: "Programas",
    component: <HubStartupChallengePage />,
    features: [USER_FEATURES.IDEA, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.STARTUP.GET_CHALLENGE,
    breadcrumb: "Visualizar programa",
    component: <HubStartupChallengeGet />,
    features: [USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.STARTUP.APPLY_CHALLENGE,
    breadcrumb: "Inscrever-se no programa",
    component: <HubStartupChallengeApplicationFormPage />,
    features: [USER_FEATURES.IDEA, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.STARTUP.IDEA_LIST,
    breadcrumb: "Minhas inscrições",
    component: <HubStartupIdeaPage />,
    features: [USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.STARTUP.GET_IDEA,
    breadcrumb: "Inscrição",
    component: <HubStartupIdeaGet />,
    features: [USER_FEATURES.IDEA, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":ideaId"] },
  },
  {
    path: ROUTES.HUB.STARTUP.EDIT_IDEA,
    breadcrumb: "Inscrição",
    component: <HubStartupChallengeApplicationEditFormPage />,
    features: [USER_FEATURES.IDEA, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":ideaId", ":challengeId"] },
  },
  {
    path: ROUTES.HUB.STARTUP.EVENTS_LIST,
    breadcrumb: "Eventos",
    component: <HubStartupEventsPage />,
    features: [USER_FEATURES.IDEA, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.STARTUP.OPPORTUNITY_LIST,
    breadcrumb: () => trans("scouting.opportunity.startup.pageTitle"),
    component: <HubStartupOpportunityPage />,
    features: [USER_FEATURES.IDEA, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.STARTUP.GET_OPPORTUNITY,
    breadcrumb: () => trans("scouting.opportunity.startup.detailBreadcrumb"),
    component: <HubStartupOpportunityGetPage />,
    features: [USER_FEATURES.IDEA, 'caixa_employee'],
    props: { params: [":opportunityId"] },
  },
  {
    path: ROUTES.HUB.STARTUP.GET_EVENT,
    breadcrumb: "Visualizar Evento",
    component: <HubResolverEventGetPage />,
    features: [USER_FEATURES.IDEA, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":eventId"] },
  },
  {
    path: ROUTES.HUB.EVALUATOR.CHALLENGE_LIST,
    breadcrumb: "Minhas avaliações",
    component: <HubEvaluatorChallengePage />,
    features: [USER_FEATURES.EVALUATION, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.EVALUATOR.EVALUATE_IDEA,
    breadcrumb: "Avaliar ideia",
    component: <HubEvaluatorIdeaEvaluatePage />,
    features: [USER_FEATURES.EVALUATION, 'caixa_employee'],
    props: { params: [":challengeId", ":stepId", ":ideaId"] },
  },
  {
    path: ROUTES.HUB.EVALUATOR.INVITES,
    breadcrumb: "Meus convites",
    component: <HubEvaluatorInvitesPage />,
    features: [USER_FEATURES.EVALUATION, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.EVALUATOR.GET_CHALLENGE,
    breadcrumb: "Visualizar programa",
    component: <HubEvaluatorChallengeInfoPage />,
    features: [USER_FEATURES.EVALUATION, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.EVALUATOR.CHALLENGE_SOLUTIONS,
    breadcrumb: "Soluções do programa",
    component: <HubEvaluatorChallengeSolutionsPage />,
    features: [USER_FEATURES.EVALUATION, 'caixa_employee'],
    props: { params: [":challengeId", ":stepId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.CHALLENGE_SOLUTIONS_SUBSCRIPTIONS,
    breadcrumb: "Visualizar Inscrição",
    component: <HubCompanyChallengeSolutionsInfoPage />,
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":challengeId", ":solutionId"] },
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_LIST,
    breadcrumb: "Trilhas",
    component: <TracksPage />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_CREATE,
    breadcrumb: "Criar trilha",
    component: <CreateTrackForm />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_EDIT,
    breadcrumb: "Editar trilha",
    component: <EditTrackForm />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":trackId"] },
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_AVAILABLE,
    breadcrumb: "Trilhas disponíveis",
    component: <TracksAvailablePage />,
    features: [USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.TRACK.LEARNING,
    breadcrumb: "Meus aprendizados",
    component: <LearningPage />,
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.ECOSYSTEM_PUBLIC_ADMINISTRATION,
    breadcrumb: 'Detalhes do órgão público',
    component: <HubPublicAdministrationGetPage />,
    features: [
      USER_FEATURES.CHALLENGE,
      USER_FEATURES.IDEA,
      USER_FEATURES.CITY_HALL,
      USER_FEATURES.ADMIN_CITY_HALL,
      'caixa_employee',
    ],
    props: { params: [':publicAdministrationId'] }
  },
  {
    path: ROUTES.HUB.COMPANY.ECOSYSTEM_STARTUP,
    breadcrumb: 'Detalhes da startup',
    component: <HubStartupGetPage />,
    features: [
      USER_FEATURES.CHALLENGE,
      USER_FEATURES.IDEA,
      USER_FEATURES.CITY_HALL,
      USER_FEATURES.ADMIN_CITY_HALL,
      'caixa_employee',
    ],
    props: { params: [':companyId'] }
  },
  {
    path: ROUTES.HUB.TRACK.LEARNING_LESSON,
    breadcrumb: () => {
      const trackNameElement = document.querySelector('[data-track-name]');
      return trackNameElement?.textContent || '';
    },
    component: <LearningLessonPage />,
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, 'caixa_employee'],
    props: { params: [":lessonId"] },
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_AVAILABLE_DETAILS,
    breadcrumb: () => {
      const trackNameElement = document.querySelector('[data-track-name]');
      return trackNameElement?.textContent || '';
    },
    component: <TrackAvailableDetailsPage />,
    features: [USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":trackId"] },
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_AVAILABLE_LESSON,
    breadcrumb: "Aula",
    component: <TrackAvailableLessonPage />,
    features: [USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, 'caixa_employee'],
    props: { params: [":lessonId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_SETTINGS,
    breadcrumb: "Configurações de reservas",
    component: meetingRoomAllowedOnly(<HubCompanyMeetingRoomSettingsPage />),
    features: [USER_FEATURES.CHALLENGE],
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_MANAGE,
    breadcrumb: "Gerenciar Espaços",
    component: meetingRoomAllowedOnly(<HubCompanyManageMeetingRoomPage />),
    features: [USER_FEATURES.CHALLENGE],
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_NEW,
    breadcrumb: "Novo espaço",
    component: meetingRoomAllowedOnly(<HubCompanyNewMeetingRoomPage />),
    features: [USER_FEATURES.CHALLENGE],
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_DETAIL,
    breadcrumb: "Detalhe do espaço",
    component: meetingRoomAllowedOnly(<HubCompanyMeetingRoomDetailPage />),
    features: [USER_FEATURES.CHALLENGE],
    props: { params: [':meetingRoomId'] },
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_EDIT,
    breadcrumb: "Editar espaço",
    component: meetingRoomAllowedOnly(<HubCompanyEditMeetingRoomPage />),
    features: [USER_FEATURES.CHALLENGE],
    props: { params: [':meetingRoomId'] },
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_RESERVED,
    breadcrumb: "Espaços reservados",
    component: meetingRoomAllowedOnly(<HubCompanyMeetingRoomReservedPage />),
    features: [USER_FEATURES.CHALLENGE],
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_REQUESTS,
    breadcrumb: "Gerenciar solicitações",
    component: meetingRoomAllowedOnly(<HubCompanyMeetingRoomRequestsPage />),
    features: [USER_FEATURES.CHALLENGE],
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_RESERVE,
    breadcrumb: "Reservar espaço",
    component: meetingRoomAllowedOnly(<HubCompanyMeetingRoomReservePage />),
    features: [
      USER_FEATURES.CHALLENGE,
      USER_FEATURES.IDEA,
      USER_FEATURES.CITY_HALL,
      USER_FEATURES.ADMIN_CITY_HALL,
      "caixa_employee",
    ],
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_MY_RESERVATIONS,
    breadcrumb: "Minhas reservas",
    component: meetingRoomAllowedOnly(<HubCompanyMeetingRoomMyReservationsPage />),
    features: [
      USER_FEATURES.CHALLENGE,
      USER_FEATURES.IDEA,
      USER_FEATURES.CITY_HALL,
      USER_FEATURES.ADMIN_CITY_HALL,
      "caixa_employee",
    ],
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_GET,
    breadcrumb: () => {
      const trackNameElement = document.querySelector('[data-track-name]');
      return trackNameElement?.textContent || '';
    },
    component: <TrackGetPage />,
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":trackId"] },
  },
];
